import React from "react";
import type { Control, FieldPath } from "react-hook-form";
import { z } from "zod";

import ArabicDatePicker from "@/app/global-components/arbic-date-picker";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/global-components/form";
import { ComboboxView } from "@/app/global-components/combobox-view";
import { createTenderQuotationSchema } from "../../../vuetender/components/tenderQuotationSchema";
import {
  Box,
  Typography,
  FormControl as MuiFormControl,
  FormControlLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
  TextField,
  Chip,
  IconButton,
  Avatar,
  useTheme,
  alpha,
  Autocomplete,
} from "@mui/material";
import { Clear, Upload, Description } from "@mui/icons-material";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG } from "@/types";
import {
  InsertDriveFile as FileText,
  Image as ImageIcon,
} from "@mui/icons-material";

const tenderQuotationSchema = createTenderQuotationSchema((key: string) => key);

interface TenderFieldViewProps {
  name: FieldPath<z.infer<typeof tenderQuotationSchema>>;
  label: string;
  placeholder: string;
  description?: string;
  inputType?:
    | "text"
    | "select"
    | "radio"
    | "date"
    | "combobox"
    | "number"
    | "textarea"
    | "multiselect"
    | "Checkbox"
    | "file";
  options?: { value: string; label: string }[];
  formControl: Control<z.infer<typeof tenderQuotationSchema>>;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | string
  ) => void;
}

const QuatationFieldView: React.FC<TenderFieldViewProps> = ({
  name,
  label,
  placeholder,
  inputType,
  options,
  formControl,
  onChange,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return <Description color="error" />;
      case "doc":
      case "docx":
        return <FileText color="primary" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <ImageIcon color="success" />;
      default:
        return <FileText color="action" />;
    }
  };

  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field }) => (
        <FormItem className="mb-4">
          {label && (
            <FormLabel className="mb-2 text-xs lg:text-base font-semibold text-gray-700">
              {label}
            </FormLabel>
          )}
          <FormControl>
            {inputType === "radio" && options ? (
              <MuiFormControl component="fieldset">
                <MuiRadioGroup
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  sx={{
                    "& .MuiFormControlLabel-root": {
                      margin: "4px 0",
                      padding: "8px 12px",
                      borderRadius: 1,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.04
                        ),
                      },
                      "&.Mui-checked": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.08
                        ),
                      },
                    },
                    "& .MuiRadio-root": {
                      color: theme.palette.grey[400],
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  {options.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.label}
                        </Typography>
                      }
                    />
                  ))}
                </MuiRadioGroup>
              </MuiFormControl>
            ) : inputType === "date" ? (
              <Box sx={{ position: "relative" }}>
                <ArabicDatePicker
                  date={
                    field.value && typeof field.value === "string"
                      ? new Date(field.value)
                      : null
                  }
                  setDate={(date) => {
                    if (date) {
                      const localDateString = date.toLocaleDateString("en-CA");
                      field.onChange(localDateString);
                    } else {
                      field.onChange(null);
                    }
                  }}
                />
              </Box>
            ) : inputType === "combobox" && options ? (
              <ComboboxView
                placeholder={placeholder}
                options={options}
                value={field.value as string}
                onChange={(value) => field.onChange(value)}
              />
            ) : inputType === "textarea" ? (
              <TextField
                {...field}
                multiline
                rows={3}
                placeholder={placeholder}
                value={typeof field.value === "string" ? field.value : ""}
                variant="outlined"
                fullWidth
                sx={{
                  minWidth: 300,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    "&[data-shrink='false']": {
                      pr: locale === LANG.AR ? "20px" : "0px",
                    },
                  },
                }}
              />
            ) : inputType === "select" && options ? (
              <Autocomplete
                options={options}
                getOptionLabel={(option) => option.label}
                value={options.find((opt) => opt.value === field.value) || null}
                onChange={(_, newValue) => {
                  if (onChange) {
                    onChange(newValue ? newValue.value : "");
                  } else {
                    field.onChange(newValue ? newValue.value : "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={placeholder}
                    variant="outlined"
                    fullWidth
                    sx={{
                      minWidth: 300,
                      borderRadius: 2,
                      direction: locale === LANG.AR ? "rtl" : "ltr",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        "&[data-shrink='false']": {
                          pr: locale === LANG.AR ? "20px" : "0px",
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                disableClearable={false}
                dir={locale === LANG.AR ? "rtl" : "ltr"}
              />
            ) : inputType === "file" ? (
              <Box sx={{ position: "relative" }}>
                <TextField
                  type="text"
                  value={field.value instanceof File ? field.value.name : ""}
                  placeholder={t("inputs.placeholders.file")}
                  variant="outlined"
                  fullWidth
                  onClick={() =>
                    document.getElementById(`file-upload-${name}`)?.click()
                  }
                  sx={{
                    minWidth: 150,
                    cursor: "pointer",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.grey[50], 0.5),
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.grey[100], 0.5),
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.grey[300],
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main,
                    },
                    "& .MuiInputLabel-root": {
                      "&[data-shrink='false']": {
                        pr: locale === LANG.AR ? "20px" : "0px",
                      },
                    },
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: field.value ? (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          field.onChange(null);
                        }}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Clear />
                      </IconButton>
                    ) : (
                      <IconButton
                        sx={{ color: theme.palette.grey[500] }}
                        onClick={(e) => {
                          e.stopPropagation();
                          document
                            .getElementById(`file-upload-${name}`)
                            ?.click();
                        }}
                      >
                        <Upload />
                      </IconButton>
                    ),
                  }}
                />
                <input
                  id={`file-upload-${name}`}
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const fileType = file.type;
                      const validTypes = [
                        "image/jpeg",
                        "image/png",
                        "image/gif",
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      ];
                      if (validTypes.includes(fileType)) {
                        field.onChange(file);
                      } else {
                        alert(t("alerts.error.uploadDocument"));
                        field.onChange(null);
                      }
                    } else {
                      field.onChange(null);
                    }
                    e.target.value = "";
                  }}
                />
                {field.value && field.value instanceof File && (
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: theme.palette.grey[200],
                      }}
                    >
                      {getFileIcon(field.value.name)}
                    </Avatar>
                    <Typography variant="caption" color="text.secondary">
                      {field.value.name}
                    </Typography>
                    <Chip
                      label={`${(field.value.size / 1024 / 1024).toFixed(
                        2
                      )} MB`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: "auto" }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <TextField
                {...field}
                type={inputType || "text"}
                placeholder={placeholder}
                value={field.value?.toString() || ""}
                variant="outlined"
                fullWidth
                onChange={(e) => {
                  const value =
                    inputType === "number"
                      ? parseFloat(e.target.value) || 0
                      : e.target.value;
                  field.onChange(value);
                  if (onChange) {
                    onChange(e as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                inputProps={{
                  min: inputType === "number" ? 0 : undefined,
                  step: inputType === "number" ? 1 : undefined,
                }}
                sx={{
                  minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                    "&[data-shrink='false']": {
                      pr: locale === LANG.AR ? "20px" : "0px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            )}
          </FormControl>
          <FormMessage className="text-red-500 text-xs sm:text-sm mt-1" />
        </FormItem>
      )}
    />
  );
};

export default QuatationFieldView;
